/**
 * Keyboard + mouse state sampled per frame, plus virtual input injected by on-screen touch controls.
 * `down` = currently held, `pressed` = went down this frame, `released` = went up this frame.
 */
export class Input {
  private held = new Set<string>();
  private pressedSet = new Set<string>();
  private releasedSet = new Set<string>();
  mouseX = 0;
  mouseY = 0;
  /** No pointer aim until the player actually points at the canvas. */
  mouseKnown = false;
  private mouseClient: [number, number] = [0, 0];
  /** mouse in internal canvas coords */
  mouseDown = false;
  mousePressed = false;
  mouseRight = false;
  mouseRightPressed = false;
  wheel = 0;
  private wheelAcc = 0;
  /** text typed this frame (for code entry) */
  typed = '';
  private typedAcc = '';
  anyKeyPressed = false;
  private anyAcc = false;
  /** Analog movement from a virtual stick: a unit direction, or zero when idle. */
  moveX = 0;
  moveY = 0;
  /** Flashlight direction from a virtual aim stick; null while that stick is idle. */
  aimAngle: number | null = null;
  /** Last pointer position of any kind (mouse or touch tap) in internal coords, for menus and the map. */
  pointerX = 0;
  pointerY = 0;
  pointerKnown = false;
  /** A touch tap on the canvas this frame (never a mouse click). */
  tapped = false;
  tapX = 0;
  tapY = 0;
  private tapQueue: [number, number] | null = null;
  /** A touch screen is in use: HUD hints describe on-screen buttons instead of keys. */
  touch = false;
  /** Runs synchronously inside real user gestures so audio can be unlocked on mobile browsers. */
  onGesture: (() => void) | null = null;

  constructor(canvas: HTMLCanvasElement, private toInternal: (cx: number, cy: number) => [number, number]) {
    window.addEventListener('keydown', (e) => {
      this.onGesture?.();
      if (e.repeat) {
        return;
      }
      const k = norm(e.code);
      if (!this.held.has(k)) this.pressedSet.add(k);
      this.held.add(k);
      this.anyAcc = true;
      if (e.key.length === 1) this.typedAcc += e.key;
      if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.code)) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => {
      const k = norm(e.code);
      this.held.delete(k);
      this.releasedSet.add(k);
    });
    window.addEventListener('blur', () => this.reset());
    const point = (e: MouseEvent) => {
      this.mouseKnown = true;
      this.mouseClient = [e.clientX, e.clientY];
      const [x, y] = toInternal(e.clientX, e.clientY);
      this.mouseX = x;
      this.mouseY = y;
      this.pointerX = x;
      this.pointerY = y;
      this.pointerKnown = true;
    };
    canvas.addEventListener('mousemove', point);
    window.addEventListener('resize', () => {
      if (this.mouseKnown) {
        [this.mouseX, this.mouseY] = toInternal(...this.mouseClient);
        [this.pointerX, this.pointerY] = [this.mouseX, this.mouseY];
      }
    });
    canvas.addEventListener('mousedown', (e) => {
      e.preventDefault();
      this.onGesture?.();
      point(e);
      if (e.button === 0) {
        this.mouseDown = true;
        this.mousePressed = true;
      } else if (e.button === 2) {
        this.mouseRight = true;
        this.mouseRightPressed = true;
      }
      this.anyAcc = true;
    });
    window.addEventListener('mouseup', (e) => {
      if (e.button === 0) this.mouseDown = false;
      if (e.button === 2) this.mouseRight = false;
    });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.wheelAcc += Math.sign(e.deltaY);
    }, { passive: false });
  }

  /** Forget held and queued input without changing the last aim position. */
  reset(): void {
    this.held.clear();
    this.endFrame();
    this.mouseDown = false; this.mouseRight = false;
    this.wheel = 0; this.wheelAcc = 0;
    this.typed = ''; this.typedAcc = '';
    this.anyKeyPressed = false; this.anyAcc = false;
    this.moveX = 0; this.moveY = 0; this.aimAngle = null;
    this.tapped = false; this.tapQueue = null;
  }

  /** Call once at the start of each frame. */
  beginFrame(): void {
    this.wheel = this.wheelAcc;
    this.wheelAcc = 0;
    this.typed = this.typedAcc;
    this.typedAcc = '';
    this.anyKeyPressed = this.anyAcc;
    this.anyAcc = false;
    if (this.tapQueue) { [this.tapX, this.tapY] = this.tapQueue; this.tapped = true; this.tapQueue = null; }
  }
  /** Call once at the end of each frame. */
  endFrame(): void {
    this.pressedSet.clear();
    this.releasedSet.clear();
    this.mousePressed = false;
    this.mouseRightPressed = false;
    this.tapped = false;
  }
  down(code: string): boolean {
    return this.held.has(code);
  }
  pressed(code: string): boolean {
    return this.pressedSet.has(code);
  }
  released(code: string): boolean {
    return this.releasedSet.has(code);
  }
  anyDown(...codes: string[]): boolean {
    return codes.some((c) => this.held.has(c));
  }
  anyPressed(...codes: string[]): boolean {
    return codes.some((c) => this.pressedSet.has(c));
  }
  /** Convenience: consume a press so other layers don't see it this frame. */
  consume(code: string): void {
    this.pressedSet.delete(code);
  }
  consumeMouse(): void {
    this.mousePressed = false;
  }

  // ---------------- virtual input (touch controls) ----------------
  /** Hold a key as if it were pressed on a keyboard. */
  press(code: string): void {
    if (!this.held.has(code)) this.pressedSet.add(code);
    this.held.add(code);
    this.anyAcc = true;
    this.anyKeyPressed = true;
  }
  release(code: string): void {
    if (!this.held.has(code)) return;
    this.held.delete(code);
    this.releasedSet.add(code);
  }
  /** A momentary key: registers as pressed for the current frame without staying held. */
  tapKey(code: string): void {
    this.press(code);
    this.release(code);
  }
  /** Characters entered from an on-screen keypad. */
  typeText(s: string): void {
    this.typedAcc += s;
    this.anyAcc = true;
  }
  /** A touch tap on the canvas, in client coordinates. Menus resolve it on the next frame. */
  tap(cx: number, cy: number): void {
    const [x, y] = this.toInternal(cx, cy);
    this.pointerX = x; this.pointerY = y; this.pointerKnown = true;
    this.tapQueue = [x, y];
    this.anyAcc = true;
  }
}

function norm(code: string): string {
  return code;
}
